import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Calendar } from 'lucide-react';
import { Button } from './ui/button';

const CalendarWidget = ({ selectedDate, selectedTime, onDateSelect, onTimeSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookedTimes, setBookedTimes] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availableSlots] = useState({
    // Available time slots for each day (in 24h format)
    monday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
    tuesday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
    wednesday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
    thursday: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
    friday: ['09:00', '10:00', '11:00', '14:00', '15:00'],
    saturday: ['10:00', '11:00', '14:00', '15:00', '16:00'], // Weekend availability enabled
    sunday: ['10:00', '11:00', '14:00', '15:00', '16:00']   // Weekend availability enabled
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Empty cells for previous month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const today = new Date();
      
      // Only show future dates (including today)
      const isAvailable = date >= today.setHours(0, 0, 0, 0);
      const dayOfWeek = date.getDay();
      const hasSlots = availableSlots[dayNames[dayOfWeek]].length > 0;
      
      days.push({
        date: day,
        fullDate: date,
        isAvailable: isAvailable && hasSlots,
        isToday: date.toDateString() === new Date().toDateString(),
        isSelected: selectedDate === formatDate(date)
      });
    }
    
    return days;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleDateClick = (day) => {
    if (!day || !day.isAvailable) return;
    
    const dateStr = formatDate(day.fullDate);
    onDateSelect(dateStr);
    
    // Reset time selection when date changes
    if (selectedDate !== dateStr) {
      onTimeSelect(null);
    }
  };

  // Fetch booked times for selected date
  const fetchBookedTimes = async (dateStr) => {
    if (!dateStr) return;
    
    setLoadingAvailability(true);
    try {
      // Convert date string to ISO format for backend WITHOUT timezone conversion
      const date = new Date(dateStr);
      // Use local date components to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const isoDateStr = `${year}-${month}-${day}`;
      
      // Get API base URL
      const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const apiUrl = `${API_BASE}/api/bookings/availability?date=${encodeURIComponent(isoDateStr)}`;
      
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        setBookedTimes(data.booked_times || []);
      } else {
        console.error('Failed to fetch availability');
        setBookedTimes([]);
      }
    } catch (error) {
      console.error('❌ Error fetching availability:', error);
      setBookedTimes([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Reset booked times when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchBookedTimes(selectedDate);
    } else {
      setBookedTimes([]);
    }
  }, [selectedDate]);

  const getAvailableTimesForSelectedDate = () => {
    if (!selectedDate) return [];
    
    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay();
    const allSlots = availableSlots[dayNames[dayOfWeek]] || [];
    
    // Filter out booked times
    return allSlots.filter(timeSlot => {
      const formattedTime = formatTime(timeSlot);
      return !bookedTimes.includes(formattedTime);
    });
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => navigateMonth(-1)}
          className="btn-secondary p-2"
        >
          <ChevronLeft size={20} />
        </Button>
        
        <h3 className="heading-3">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <Button
          onClick={() => navigateMonth(1)}
          className="btn-secondary p-2"
        >
          <ChevronRight size={20} />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {/* Weekday Headers */}
        {weekdays.map(day => (
          <div key={day} className="text-center p-2 text-sm font-medium text-gray-400">
            {day}
          </div>
        ))}
        
        {/* Calendar Days */}
        {calendarDays.map((day, index) => (
          <div
            key={index}
            className={`
              aspect-square flex items-center justify-center text-sm cursor-pointer transition-all duration-200
              ${!day ? '' : 
                day.isAvailable 
                  ? `hover:bg-turquoise-600/20 ${day.isSelected ? 'bg-turquoise-600 text-black' : 'text-white'}` 
                  : 'text-gray-600 cursor-not-allowed'
              }
              ${day?.isToday ? 'border border-turquoise-400' : ''}
            `}
            onClick={() => handleDateClick(day)}
          >
            {day?.date}
          </div>
        ))}
      </div>

      {/* Time Slot Selection */}
      {selectedDate && (
        <div className="mt-8">
          <h4 className="heading-4 mb-4 flex items-center gap-2">
            <Clock className="turquoise-text" size={20} />
            Available Times for {selectedDate} (IST)
          </h4>
          
          {loadingAvailability ? (
            <div className="text-center p-6 bg-gray-800/50 border border-gray-600">
              <Clock size={24} className="mx-auto text-turquoise-400 mb-2 animate-spin" />
              <p className="body-medium text-gray-400">Checking availability...</p>
            </div>
          ) : getAvailableTimesForSelectedDate().length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {getAvailableTimesForSelectedDate().map((time) => (
                <Button
                  key={time}
                  onClick={() => onTimeSelect(formatTime(time))}
                  className={`
                    py-3 transition-all duration-200
                    ${selectedTime === formatTime(time) 
                      ? 'btn-primary' 
                      : 'btn-secondary hover:bg-turquoise-600/20'
                    }
                  `}
                >
                  {formatTime(time)}
                </Button>
              ))}
            </div>
          ) : (
            <div className="text-center p-6 bg-gray-800/50 border border-gray-600">
              <Clock size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="body-medium text-gray-400">
                {bookedTimes.length > 0 ? 'All time slots are booked for this date' : 'No available slots for this date'}
              </p>
              <p className="body-small text-gray-500 mt-1">Please select a different date</p>
            </div>
          )}
        </div>
      )}

      {/* Business Hours Information */}
      <div className="mt-6 p-4 bg-turquoise-900/10 border border-turquoise-500/30">
        <h5 className="body-medium font-semibold mb-2 turquoise-text">Business Hours (IST)</h5>
        <div className="space-y-1 text-sm text-gray-300">
          <div>Monday - Thursday: 9:00 AM - 4:00 PM</div>
          <div>Friday: 9:00 AM - 3:00 PM</div>
          <div>Saturday - Sunday: 10:00 AM - 4:00 PM</div>
        </div>
        <p className="body-small text-gray-400 mt-2">
          All consultations are conducted via phone or video call. Times shown are in Indian Standard Time (IST).
        </p>
      </div>
    </div>
  );
};

export default CalendarWidget;